import type { GetStaticPaths } from 'next'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { Seo } from '@/layout/Seo'
import { NextPageWithLayout } from '@/layouts/types'
import { ComingSoonNode, getComingSoonCollection } from '@/services/ComingSoonService'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { ComingSoonView } from '@/views/ComingSoonView'

interface ComingSoonPageProps {
  comingSoonNodes: ComingSoonNode[]
  locale: string
}

const ComingSoonPage: NextPageWithLayout<ComingSoonPageProps> = ({ locale, ...rest }) => {
  const { t } = useTranslate('coming-soon')

  const url = `${getBasePath(locale)}${paths.home}`
  const title = t('comingSoonPageMetaTitleAngelv1', 'Explore Upcoming Movies & Shows | Angel')
  const description = t(
    'comingSoonPageMetaDescriptionAngel',
    'Get a first look at upcoming shows and movies coming soon to Angel!',
  )

  return (
    <>
      <Seo
        canonical={url}
        title={title}
        description={description}
        openGraph={{ title, description, url }}
        path={paths.comingSoon.index}
      />
      <ComingSoonView {...rest} />
    </>
  )
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps = getStaticPropsErrorHandler<ComingSoonPageProps>(
  { filePath: 'pages/[locale]/coming-soon/index.tsx' },
  async ({ locale }) => {
    const comingSoonNodes = await getComingSoonCollection()

    return {
      props: {
        comingSoonNodes,
        locale,
        ...(await loadTranslations(locale, ['common', 'account', 'coming-soon', 'guild'])),
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export default ComingSoonPage
